/**
 * Nouveau dossier d'arbitrage — création + dépôt (espace Avocat, Phase 3).
 *
 * Parcours en 2 étapes, aligné sur le backend (PR Phases 1-2 & 5) :
 *  1) Renseignements indispensables -> POST /api/v1/avocat/dossiers (brouillon,
 *     numéro attribué automatiquement) ;
 *  2) Téléversement des pièces (la preuve d'envoi à la partie adverse est
 *     obligatoire) puis dépôt -> POST .../soumettre (brouillon -> ouvert,
 *     recevabilité ensuite par le secrétariat). Un accusé est envoyé par email.
 */
import { useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Upload, Send } from "lucide-react";

import {
  creerDossierArbitrage,
  televerserPiece,
  soumettreDossier,
  type PartieInput,
  type PartieQualite,
  type ArbitreProposeInput,
  type Qualite,
  type AvocatPiece,
} from "@/lib/avocatApi";

const GOLD = "oklch(0.695 0.105 68)";
const BORDER = "color-mix(in oklch, oklch(0.695 0.105 68) 18%, transparent)";
const RED = "oklch(0.62 0.21 25)";

// Typologie des parties (cf. backend : DEMANDEUR | DEFENDEUR | TIERS_*).
const QUALITE_OPTIONS: { value: PartieQualite; label: string }[] = [
  { value: "DEMANDEUR", label: "Demandeur" },
  { value: "DEFENDEUR", label: "Défendeur" },
  { value: "TIERS_VOLONTAIRE", label: "Tiers intervenant volontaire" },
  { value: "TIERS_FORCE", label: "Tiers intervenant forcé" },
];

/** Message lisible à partir d'une erreur axios (422 FastAPI → liste des champs). */
function serverErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } }).response
    ?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (d as { msg?: string }).msg)
      .filter((m): m is string => Boolean(m));
    if (msgs.length) return msgs.join(" · ");
  }
  return fallback;
}

const PIECE_CATEGORIES: { value: string; label: string; obligatoire?: boolean }[] = [
  { value: "requete", label: "Requête d'arbitrage" },
  { value: "convention_arbitrage", label: "Convention d'arbitrage" },
  {
    value: "preuve_envoi_partie_adverse",
    label: "Preuve d'envoi à la partie adverse",
    obligatoire: true,
  },
  { value: "preuve_paiement_frais", label: "Preuve de paiement des frais (facultatif)" },
  { value: "piece_jointe", label: "Pièce jointe (bordereau)" },
  { value: "autre", label: "Autre" },
];

const inputCls =
  "mt-1 block w-full rounded-md border px-2 py-1.5 text-sm bg-transparent";
const inputStyle = { borderColor: BORDER } as const;

function Field(props: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span style={{ color: GOLD }}>
        {props.label}
        {props.required ? " *" : ""}
      </span>
      {props.children}
    </label>
  );
}

const EMPTY_PARTIE: PartieInput = {
  nom: "",
  qualite: "DEMANDEUR",
  adresse: "",
  domicile_elu: "",
};

export default function NouveauDossier() {
  const [, setLocation] = useLocation();

  // Étape 1 — intake
  const [objetLitige, setObjetLitige] = useState("");
  const [partieRepresentee, setPartieRepresentee] = useState<Qualite>("DEMANDEUR");
  const [parties, setParties] = useState<PartieInput[]>([
    { ...EMPTY_PARTIE, qualite: "DEMANDEUR" },
    { ...EMPTY_PARTIE, qualite: "DEFENDEUR" },
  ]);
  const [montant, setMontant] = useState("");
  const [devise, setDevise] = useState("XOF");
  const [convention, setConvention] = useState("");
  const [resume, setResume] = useState("");
  const [arbitres, setArbitres] = useState<ArbitreProposeInput[]>([{ nom: "" }]);
  const [siege, setSiege] = useState("");
  const [langue, setLangue] = useState("");
  const [droit, setDroit] = useState("");
  const [souhaits, setSouhaits] = useState("");

  // Étape 2 — dossier créé + pièces
  const [dossierId, setDossierId] = useState<number | null>(null);
  const [reference, setReference] = useState<string>("");
  const [pieces, setPieces] = useState<AvocatPiece[]>([]);
  const [categorie, setCategorie] = useState("preuve_envoi_partie_adverse");
  const [bordereau, setBordereau] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const aPreuveEnvoi = pieces.some((p) => p.categorie === "preuve_envoi_partie_adverse");

  // --- Validation par champ (feedback rouge + aide au survol) -------------
  const montantNum = Number(montant);
  const errObjet = !objetLitige.trim();
  const errConvention = !convention.trim();
  const errResume = !resume.trim();
  const errMontant = montant.trim() === "" || Number.isNaN(montantNum) || montantNum < 0;
  const partiesCompletes = parties.filter((p) => p.nom.trim() && p.adresse.trim());
  const errParties = partiesCompletes.length === 0;
  const errArbitres = arbitres.filter((a) => a.nom.trim()).length === 0;
  const siegeComplet = Boolean(siege.trim() && langue.trim() && droit.trim());
  const errDefaut = !siegeComplet && !souhaits.trim();
  const formInvalide =
    errObjet || errConvention || errResume || errMontant || errParties || errArbitres || errDefaut;

  // Une partie partiellement saisie est silencieusement ignorée : on la signale.
  const partieNomErr = (p: PartieInput, i: number) =>
    !p.nom.trim() && (Boolean(p.adresse.trim()) || (errParties && i === 0));
  const partieAdrErr = (p: PartieInput, i: number) =>
    !p.adresse.trim() && (Boolean(p.nom.trim()) || (errParties && i === 0));
  const red = (bad: boolean) => ({ borderColor: showErrors && bad ? RED : BORDER });

  function setPartie(i: number, patch: Partial<PartieInput>) {
    setParties((arr) => arr.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  }
  function setArbitre(i: number, nom: string) {
    setArbitres((arr) => arr.map((a, idx) => (idx === i ? { nom } : a)));
  }

  async function onCreate() {
    if (formInvalide) {
      setShowErrors(true);
      toast.error("Complétez les champs surlignés en rouge.");
      return;
    }
    setBusy(true);
    try {
      const res = await creerDossierArbitrage({
        objet_litige: objetLitige.trim(),
        partie_representee: partieRepresentee,
        parties: partiesCompletes.map((p) => ({
          ...p,
          domicile_elu: p.domicile_elu || null,
        })),
        montant_demande: montantNum,
        montant_devise: devise || "XOF",
        convention_arbitrage: convention.trim(),
        resume_pretentions: resume.trim(),
        arbitres_proposes: arbitres.filter((a) => a.nom.trim()),
        siege: siege.trim() || null,
        langue: langue.trim() || null,
        droit_applicable: droit.trim() || null,
        souhaits_defaut: souhaits.trim() || null,
      });
      setDossierId(res.id);
      setReference(res.reference);
      toast.success(`Dossier créé — n° ${res.reference} (brouillon).`);
    } catch (err) {
      toast.error(serverErrorMessage(err, "Échec de la création du dossier."));
    } finally {
      setBusy(false);
    }
  }

  async function onUpload() {
    if (!dossierId || !file) {
      toast.error("Sélectionnez un fichier.");
      return;
    }
    setBusy(true);
    try {
      const p = await televerserPiece({
        dossier_id: dossierId,
        categorie,
        file,
        bordereau_number: bordereau || undefined,
        obligatoire: categorie === "preuve_envoi_partie_adverse",
      });
      setPieces((arr) => [...arr, p]);
      setFile(null);
      setBordereau("");
      toast.success("Pièce téléversée.");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      // eslint-disable-next-line no-console
      console.error("[upload] échec", err);
      toast.error(detail ?? "Échec du téléversement.");
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit() {
    if (!dossierId) return;
    setBusy(true);
    try {
      const res = await soumettreDossier(dossierId);
      toast.success(`Dossier déposé — ${res.reference} (${res.statut}).`);
      setLocation("/avocat");
    } catch {
      toast.error(
        "Dépôt refusé : vérifiez que la preuve d'envoi à la partie adverse est jointe.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <Link href="/avocat" className="inline-flex items-center gap-1 text-sm opacity-80">
        <ArrowLeft size={14} /> Retour à l'espace avocat
      </Link>
      <h1 className="mt-2 text-xl font-semibold">Nouveau dossier d'arbitrage</h1>

      {dossierId === null ? (
        <div className="mt-4 space-y-4 rounded-md border p-4" style={{ borderColor: BORDER }}>
          <Field label="Objet du litige" required>
            <input className={inputCls} style={red(errObjet)} value={objetLitige}
              onChange={(e) => setObjetLitige(e.target.value)} />
          </Field>

          <Field label="Vous représentez" required>
            <select className={inputCls} style={inputStyle} value={partieRepresentee}
              onChange={(e) => setPartieRepresentee(e.target.value as Qualite)}>
              <option value="DEMANDEUR">Le demandeur</option>
              <option value="DEFENDEUR">Le défendeur</option>
            </select>
          </Field>

          <div>
            <div className="text-sm" style={{ color: GOLD }}>Parties (identité, adresse, domicile élu) *</div>
            {parties.map((p, i) => (
              <div key={i} className="mt-2 grid grid-cols-1 gap-2 rounded-md border p-2 sm:grid-cols-2"
                style={{ borderColor: BORDER }}>
                <input className={inputCls} style={red(partieNomErr(p, i))} placeholder="Nom / dénomination"
                  value={p.nom} onChange={(e) => setPartie(i, { nom: e.target.value })} />
                <select className={inputCls} style={inputStyle} value={p.qualite}
                  onChange={(e) => setPartie(i, { qualite: e.target.value as PartieQualite })}>
                  {QUALITE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <input className={inputCls} style={red(partieAdrErr(p, i))} placeholder="Adresse"
                  value={p.adresse} onChange={(e) => setPartie(i, { adresse: e.target.value })} />
                <input className={inputCls} style={inputStyle} placeholder="Domicile élu (optionnel)"
                  value={p.domicile_elu ?? ""} onChange={(e) => setPartie(i, { domicile_elu: e.target.value })} />
                {parties.length > 1 && (
                  <button type="button" className="text-xs opacity-70 inline-flex items-center gap-1"
                    onClick={() => setParties((arr) => arr.filter((_, idx) => idx !== i))}>
                    <Trash2 size={12} /> Retirer
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="mt-2 inline-flex items-center gap-1 text-xs"
              style={{ color: GOLD }} onClick={() => setParties((arr) => [...arr, { ...EMPTY_PARTIE }])}>
              <Plus size={12} /> Ajouter une partie
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Field label="Montant de la demande" required>
                <input className={inputCls} style={red(errMontant)} inputMode="decimal"
                  value={montant} onChange={(e) => setMontant(e.target.value)} />
              </Field>
            </div>
            <Field label="Devise">
              <input className={inputCls} style={inputStyle} value={devise}
                onChange={(e) => setDevise(e.target.value)} />
            </Field>
          </div>

          <Field label="Convention d'arbitrage (clause / référence)" required>
            <textarea className={inputCls} style={red(errConvention)} rows={2} value={convention}
              onChange={(e) => setConvention(e.target.value)} />
          </Field>

          <Field label="Résumé des prétentions et moyens" required>
            <textarea className={inputCls} style={red(errResume)} rows={4} value={resume}
              onChange={(e) => setResume(e.target.value)} />
          </Field>

          <div>
            <div className="text-sm" style={{ color: GOLD }}>Arbitres proposés (nombre + désignation) *</div>
            {arbitres.map((a, i) => (
              <div key={i} className="mt-2 flex gap-2">
                <input className={inputCls} style={red(errArbitres && !a.nom.trim())} placeholder="Nom de l'arbitre proposé"
                  value={a.nom} onChange={(e) => setArbitre(i, e.target.value)} />
                {arbitres.length > 1 && (
                  <button type="button" className="text-xs opacity-70"
                    onClick={() => setArbitres((arr) => arr.filter((_, idx) => idx !== i))}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="mt-2 inline-flex items-center gap-1 text-xs"
              style={{ color: GOLD }} onClick={() => setArbitres((arr) => [...arr, { nom: "" }])}>
              <Plus size={12} /> Ajouter un arbitre
            </button>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Field label="Siège"><input className={inputCls} style={red(errDefaut && !siege.trim())} value={siege}
              onChange={(e) => setSiege(e.target.value)} /></Field>
            <Field label="Langue"><input className={inputCls} style={red(errDefaut && !langue.trim())} value={langue}
              onChange={(e) => setLangue(e.target.value)} /></Field>
            <Field label="Droit applicable"><input className={inputCls} style={red(errDefaut && !droit.trim())} value={droit}
              onChange={(e) => setDroit(e.target.value)} /></Field>
          </div>
          <Field label="Médiation préalable & observations (à défaut d'accord sur siège, langue, droit)">
            <textarea className={inputCls} style={red(errDefaut)} rows={2} value={souhaits}
              placeholder="Ex. : une médiation préalable est souhaitée ; ou vos souhaits sur le siège, la langue, le droit applicable."
              onChange={(e) => setSouhaits(e.target.value)} />
          </Field>

          <button type="button" disabled={busy} onClick={onCreate}
            className="rounded-md px-4 py-2 text-sm font-medium text-black disabled:opacity-50"
            style={{ background: GOLD }}
            title={formInvalide ? "Complétez les champs obligatoires (objet, parties, montant, convention, résumé, arbitres, et siège/langue/droit ou médiation)." : ""}>
            Créer le dossier (brouillon)
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-4 rounded-md border p-4" style={{ borderColor: BORDER }}>
          <div className="text-sm">
            Dossier <strong>{reference}</strong> créé en brouillon. Téléversez les pièces
            puis déposez. La <strong>preuve d'envoi à la partie adverse</strong> est obligatoire ;
            la preuve de paiement des frais est facultative.
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field label="Catégorie de pièce">
              <select className={inputCls} style={inputStyle} value={categorie}
                onChange={(e) => setCategorie(e.target.value)}>
                {PIECE_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </Field>
            <Field label="N° bordereau (optionnel)">
              <input className={inputCls} style={inputStyle} value={bordereau}
                onChange={(e) => setBordereau(e.target.value)} />
            </Field>
          </div>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
          <div>
            <button type="button" disabled={busy || !file} onClick={onUpload}
              className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
              style={{ borderColor: BORDER }}>
              <Upload size={14} /> Téléverser la pièce
            </button>
          </div>

          {pieces.length > 0 && (
            <ul className="space-y-1 text-sm">
              {pieces.map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded border px-2 py-1"
                  style={{ borderColor: BORDER }}>
                  <span>{p.filename}</span>
                  <span className="opacity-70">{p.categorie ?? "—"}</span>
                </li>
              ))}
            </ul>
          )}

          <button type="button" disabled={busy || !aPreuveEnvoi} onClick={onSubmit}
            className="inline-flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-black disabled:opacity-50"
            style={{ background: GOLD }}
            title={aPreuveEnvoi ? "" : "Joignez d'abord la preuve d'envoi à la partie adverse."}>
            <Send size={14} /> Déposer le dossier
          </button>
        </div>
      )}
    </div>
  );
}
